import { Circle, Gradient, Layout, Line, Pattern, Rect, blur, type Node } from "@revideo/2d";
import { clipSeed, timelineToSource, vary, windowStrength, type Clip } from "@hudhud/core";
import { BARS_FRACTION, GRAIN_URI, HEIGHT, WIDTH, frameToMs, grainOpacity, leakPosition, sparkles } from "@hudhud/reel/math";
import { loadImage } from "./assets";

/** Film grain: the reel's noise tile, repeated, overlay-blended. */
export function grainLayer(amount: number): Node | null {
  if (amount <= 0) return null;
  const tile = loadImage(GRAIN_URI);
  return new Rect({ width: WIDTH, height: HEIGHT, fill: new Pattern({ image: tile, repetition: "repeat" }), opacity: grainOpacity(amount), compositeOperation: "overlay" });
}

/** Cinematic letterbox bars. */
export function barsLayer(on: boolean): Node | null {
  if (!on) return null;
  const h = HEIGHT * BARS_FRACTION;
  return new Layout({ width: WIDTH, height: HEIGHT, children: [
    new Rect({ width: WIDTH, height: h, y: -HEIGHT / 2 + h / 2, fill: "#000" }),
    new Rect({ width: WIDTH, height: h, y: HEIGHT / 2 - h / 2, fill: "#000" }),
  ] });
}

/** A warm light leak drifting across the frame (screen blend); `amount` 0..1 fades it. */
export function lightLeakLayer(frame: () => number, amount: () => number): Node {
  // CSS: ellipse 45% 60% at (x, y) — a circle of radius 45 % of the width, stretched to 60 % of the height
  const rx = 0.45 * WIDTH, ry = 0.6 * HEIGHT;
  const gradient = new Gradient({ type: "radial", from: [0, 0], to: [0, 0], fromRadius: 0, toRadius: rx, stops: [
    { offset: 0, color: "rgba(255,170,80,0.95)" }, { offset: 0.35, color: "rgba(255,90,60,0.55)" }, { offset: 0.6, color: "rgba(255,60,120,0.25)" }, { offset: 0.78, color: "rgba(0,0,0,0)" },
  ] });
  const p = () => leakPosition(frame());
  return new Rect({
    width: 2 * rx, height: 2 * rx, scale: [1, ry / rx], fill: gradient, compositeOperation: "screen",
    position: () => [p().x * WIDTH - WIDTH / 2, p().y * HEIGHT - HEIGHT / 2],
    opacity: () => p().opacity * amount(),
  });
}

/** Slow golden sparkles floating up: a soft disc and two thin diamonds per particle. */
export function sparklesLayer(frame: () => number, color: string, amount: () => number): Node {
  let at = -1, ps = sparkles(0);
  const particles = () => { const f = frame(); if (f !== at) { ps = sparkles(f); at = f; } return ps; };
  const nodes = particles().map((_, i) => {
    const p = () => particles()[i];
    const diamond = (vertical: boolean) => new Line({
      points: () => { const r = p().r; return vertical ? [[0, -r * 2.6], [r * 0.5, 0], [0, r * 2.6], [-r * 0.5, 0]] : [[-r * 2.6, 0], [0, r * 0.5], [r * 2.6, 0], [0, -r * 0.5]]; },
      closed: true, fill: color, opacity: 0.8,
    });
    return new Layout({
      position: () => [p().x * WIDTH - WIDTH / 2, p().y * HEIGHT - HEIGHT / 2], opacity: () => p().a,
      children: [new Circle({ size: () => p().r * 2, fill: color, filters: () => [blur(p().r * 0.25)] }), diamond(true), diamond(false)],
    });
  });
  return new Layout({ width: WIDTH, height: HEIGHT, opacity: amount, children: nodes });
}

/** A thin golden ring framing the speaker around the face-follow centre. */
export function speakerRingLayer(clip: Clip, frame: () => number, color: string, amount: () => number): Node {
  const base = WIDTH * (0.74 + 0.08 * vary(clipSeed(clip), 7));
  const size = () => base * (1 + 0.015 * Math.sin(frame() * 0.12));
  return new Circle({
    size, y: HEIGHT * 0.42 - HEIGHT / 2, stroke: color, lineWidth: 6, shadowColor: `${color}66`, shadowBlur: 40,
    opacity: () => 0.9 * amount(),
  });
}

/** The directed layers: sparkles, ring and leak appear only in their windows (or all through when the clip-wide switch is on). */
export function directedLayers(clip: Clip, accent: string, frame: () => number, lite?: boolean): Node | null {
  if (lite) return null;
  const sourceMs = () => timelineToSource(clip.segments, frameToMs(frame()));
  const amt = (on: boolean, kind: "sparkle" | "ring" | "leak") => () => (on ? 1 : windowStrength(clip, kind, sourceMs()));
  const leak = amt(clip.effects.lightLeak, "leak"), ring = amt(clip.effects.ring, "ring"), sparkle = amt(clip.effects.sparkle, "sparkle");
  const wants = (kind: "sparkle" | "ring" | "leak", on: boolean) => on || clip.direction.moments.some((m) => m.kind === kind);
  const children: Node[] = [];
  if (wants("leak", clip.effects.lightLeak)) children.push(lightLeakLayer(frame, leak));
  if (wants("ring", clip.effects.ring)) children.push(speakerRingLayer(clip, frame, accent, ring));
  if (wants("sparkle", clip.effects.sparkle)) children.push(sparklesLayer(frame, accent, sparkle));
  return children.length ? new Layout({ width: WIDTH, height: HEIGHT, children }) : null;
}
